<template>
    <div class="filter-dialog-host">
        <button
            type="button"
            class="filter-dialog__trigger"
            :disabled="disabled"
            :aria-expanded="open"
            :aria-label="triggerLabel"
            aria-haspopup="dialog"
            @click="openDialog"
        >
            <span class="filter-dialog__trigger-text">
                <span v-if="label" class="filter-dialog__trigger-label">{{ label }}</span>
                <span class="filter-dialog__trigger-value">{{ summary }}</span>
            </span>
        </button>

        <Teleport to="body">
            <div v-if="open" class="filter-dialog">
                <button type="button" class="filter-dialog__backdrop" aria-label="Закрыть" @click="cancel" />
                <div
                    class="filter-dialog__panel"
                    role="dialog"
                    aria-modal="true"
                    :aria-labelledby="titleId"
                >
                    <header class="filter-dialog__header">
                        <h2 :id="titleId" class="filter-dialog__title">{{ title || label }}</h2>
                        <button type="button" class="filter-dialog__close" aria-label="Закрыть" @click="cancel">
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" />
                            </svg>
                        </button>
                    </header>
                    <div class="filter-dialog__body">
                        <slot />
                    </div>
                    <footer class="filter-dialog__footer">
                        <UiButton variant="primary" @click="apply">Применить</UiButton>
                        <UiButton variant="secondary" @click="cancel">Отмена</UiButton>
                    </footer>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import UiButton from '@/components/ui/forms/UiButton.vue';

let nextTitleId = 0;

const props = defineProps({
    label: { type: String, default: '' },
    title: { type: String, default: '' },
    summary: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'apply', 'cancel']);

const open = ref(false);
const titleId = `filter-dialog-title-${nextTitleId += 1}`;

const triggerLabel = computed(() => {
    const name = props.label || props.title || 'Фильтр';
    if (!props.summary) {
        return `${name}. Открыть окно`;
    }
    return `${name}: ${props.summary}. Открыть окно`;
});

function openDialog() {
    if (props.disabled || open.value) {
        return;
    }

    emit('open');
    open.value = true;
}

function apply() {
    if (!open.value) {
        return;
    }

    open.value = false;
    emit('apply');
}

function cancel() {
    if (!open.value) {
        return;
    }

    open.value = false;
    emit('cancel');
}

function onKeydown(event) {
    if (event.key === 'Escape' && open.value) {
        cancel();
    }
}

watch(open, (isOpen) => {
    if (typeof document === 'undefined') {
        return;
    }

    document.body.style.overflow = isOpen ? 'hidden' : '';
});

onMounted(() => {
    document.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown);
    if (open.value && typeof document !== 'undefined') {
        document.body.style.overflow = '';
    }
});
</script>

<style lang="scss" scoped>
@import '../../../../scss/tokens';

.filter-dialog-host {
    display: inline-flex;
    min-width: 0;
    max-width: 100%;
}

.filter-dialog__trigger {
    display: inline-flex;
    align-items: center;
    max-width: 220px;
    margin: 0;
    padding: 0.35rem 0.6rem;
    border: 1px solid $color-brand-border;
    border-radius: $radius-sm;
    background: #fff;
    color: $color-brand;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: $color-brand-alpha-soft;
    }

    &:focus-visible {
        outline: 2px solid $color-brand;
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.filter-dialog__trigger-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.05rem;
    min-width: 0;
}

.filter-dialog__trigger-label {
    font-size: 0.65rem;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 0.02em;
    color: $color-text-secondary;
}

.filter-dialog__trigger-value {
    max-width: 9.5rem;
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.2;
    color: $color-brand;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.filter-dialog {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
}

.filter-dialog__backdrop {
    position: absolute;
    inset: 0;
    border: 0;
    background: rgba(15, 23, 42, 0.45);
    cursor: pointer;
}

.filter-dialog__panel {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    width: min(100%, 420px);
    max-height: min(100%, calc(100dvh - 2rem));
    background: #fff;
    border-radius: $radius-lg;
    box-shadow: 0 16px 48px rgba(15, 23, 42, 0.2);
    overflow: hidden;
}

.filter-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-shrink: 0;
    padding: 1rem 1.1rem;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.filter-dialog__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: $color-text-primary;
}

.filter-dialog__close {
    border: 0;
    background: transparent;
    color: $color-text-secondary;
    width: 2rem;
    height: 2rem;
    border-radius: $radius-sm;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        background: $color-brand-alpha-soft;
        color: $color-brand;
    }
}

.filter-dialog__body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0.75rem 1rem;
    overflow-y: auto;
}

.filter-dialog__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    flex-shrink: 0;
    padding: 0.85rem 1rem 1rem;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
}
</style>
